import moment from 'moment-timezone';
import { weekDays, weekEnds } from '../../../../../utils/uiUtils';

// ─── Time Formatting ──────────────────────────────────────────────────────────

/**
 * "08:00" → "8:00 am"
 * Uses moment so it picks up the locale automatically.
 */
export const formatShiftTime = (time: string): string => moment(time, 'HH:mm').format('h:mm a');

/**
 * Overnight detection: start > end in HH:mm string comparison.
 * "18:00" > "02:00" → true  (Mon 6 pm – Tue 2 am)
 * "08:00" > "17:00" → false (normal day shift)
 */
export const isOvernightShift = (start?: string, end?: string): boolean => !!start && !!end && start > end;

/**
 * 2.25   → "2h 15min"
 * 10.5   → "10h 30min"
 * 8      → "8h"
 * undefined → undefined
 */
export const formatMinHours = (hours?: number): string | undefined => {
  if (hours == null) return undefined;
  const h = Math.floor(hours);
  const min = Math.round((hours - h) * 60);
  return min > 0 ? `${h}h ${min}min` : `${h}h`;
};

// ─── Minute-scale Arithmetic ──────────────────────────────────────────────────

/** Convert "HH:mm" string to total minutes since midnight. */
export const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Normalize start/end onto a continuous minute scale.
 * If end <= start (overnight), push end into next day (+1440).
 * "22:00"→"02:00"  becomes [1320, 1560] instead of [1320, 120].
 */
export const normalizeRange = (start: string, end: string): [number, number] => {
  const s = toMinutes(start);
  let e = toMinutes(end);
  if (e <= s) e += 1440;
  return [s, e];
};

/**
 * Is `point` within [start, end] at any day-alignment (previous day, same day, next day)?
 * Needed because normalizeRange anchors a range to its own start, so an overnight
 * range and a same-day range may represent overlapping wall-clock time while landing
 * on different points along the raw minute scale.
 */

const isPointCoveredAtAnyOffset = (point: number, start: number, end: number): boolean => start <= point && end >= point;

/**
 * Does this candidate slot cover the required START time point?
 * Candidate must be active AT reqStart, checked across day-alignments to
 * correctly compare overnight candidate/required ranges.
 *
 * req 07:00–15:00, can 08:00–18:00:
 *   rS=420, cS=480, cE=1080 → 480 <= 420? No → start NOT covered ✓
 *
 * req 07:00–15:00, can 06:00–18:00:
 *   rS=420, cS=360, cE=1080 → 360 <= 420 AND 1080 >= 420 → covered ✓
 */
export const isStartTimeCovered = (reqStart: string, reqEnd: string, canStart: string, canEnd: string): boolean => {
  const [rS] = normalizeRange(reqStart, reqEnd);
  const [cS, cE] = normalizeRange(canStart, canEnd);
  return isPointCoveredAtAnyOffset(rS, cS, cE);
};

/**
 * Does this candidate slot cover the required END time point?
 * Candidate must be active AT reqEnd, checked across day-alignments to
 * correctly compare overnight candidate/required ranges.
 *
 * req 07:00–15:00, can 08:00–18:00:
 *   rE=900, cS=480, cE=1080 → 480 <= 900 AND 1080 >= 900 → end IS covered ✓
 */
export const isEndTimeCovered = (reqStart: string, reqEnd: string, canStart: string, canEnd: string): boolean => {
  const [, rE] = normalizeRange(reqStart, reqEnd);
  const [cS, cE] = normalizeRange(canStart, canEnd);
  return isPointCoveredAtAnyOffset(rE, cS, cE);
};

// ─── Day Range Label ──────────────────────────────────────────────────────────

/**
 * "thu" → "Thu - Fri"
 * Wraps around from "sun" back to "mon".
 */
export const getDayRangeLabel = (day: string, t: (key: string) => string): string => {
  const allDays = [...weekDays, ...weekEnds]; // mon…fri, sat, sun
  const idx = allDays.indexOf(day);
  const nextDay = allDays[(idx + 1) % allDays.length];
  return `${t(`common:days.${day}`)} - ${t(`common:days.${nextDay}`)}`;
};
